
// Calculate streak bonus points
export const calculateStreakBonus = (streak: number): number => {
  if (streak >= 30) return 30; // Max bonus
  if (streak >= 20) return 20;
  if (streak >= 10) return 15;
  if (streak >= 5) return 10;
  if (streak >= 3) return 5;
  return 0;
};

// Get medal emoji based on streak length
export const getStreakMedal = (streak: number): { emoji: string; name: string } => {
  if (streak >= 30) return { emoji: '🏆', name: 'Platinum' };
  if (streak >= 14) return { emoji: '🥇', name: 'Gold' };
  if (streak >= 7) return { emoji: '🥈', name: 'Silver' };
  if (streak >= 3) return { emoji: '🥉', name: 'Bronze' };
  return { emoji: '', name: '' };
};

// Get a motivational message based on streak
export const getStreakMessage = (streak: number): string => {
  if (streak >= 30) return "Phenomenal! You've completed the entire program!";
  if (streak >= 20) return "Incredible commitment! You're on fire!";
  if (streak >= 14) return "Two weeks strong! Your hormones thank you!";
  if (streak >= 7) return "Amazing! A full week of hormone harmony!";
  if (streak >= 3) return "Fantastic! You're building a healthy streak!";
  if (streak === 2) return "Great job! Keep the momentum going!";
  if (streak === 1) return "Excellent start to your hormone health journey!";
  return "Let's begin your hormone harmony journey!";
};

// Calculate level based on points
export const calculateLevel = (points: number): number => {
  if (points >= 500) return 5;
  if (points >= 300) return 4;
  if (points >= 200) return 3;
  if (points >= 100) return 2;
  return 1;
};

// Calculate progress to next level
export const calculateLevelProgress = (points: number): number => {
  const level = calculateLevel(points);
  const levelThresholds = [0, 100, 200, 300, 500];
  
  const currentThreshold = levelThresholds[level - 1];
  const nextThreshold = levelThresholds[level] || levelThresholds[level - 1] + 200;
  
  const progress = ((points - currentThreshold) / (nextThreshold - currentThreshold)) * 100;
  return Math.min(Math.max(progress, 0), 100);
};

// Get confetti colors based on streak milestone
export const getConfettiColors = (streak: number): string[] => {
  if (streak >= 30) return ['#FFD700', '#E5E4E2', '#FFFFFF', '#C0C0C0']; // Gold, platinum
  if (streak >= 14) return ['#FFD700', '#FFC125', '#FFEC8B', '#FFFFFF']; // Gold
  if (streak >= 7) return ['#C0C0C0', '#E5E4E2', '#D3D3D3', '#FFFFFF']; // Silver
  return ['#CD7F32', '#A0522D', '#8B4513', '#FFFFFF']; // Bronze
};

// Mock leaderboard data - in a real app this would come from an API
export const getMockLeaderboard = () => [
  { 
    id: '1', 
    name: 'Sarah J.', 
    points: 350, 
    streak: 20, 
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    zones: {
      hormonal: 120,
      nutritional: 75,
      emotional: 95,
      cognitive: 60,
      physical: 85,
      spiritual: 40,
      social: 65
    }
  },
  { 
    id: '2', 
    name: 'Emily R.', 
    points: 420, 
    streak: 28, 
    avatar: 'https://randomuser.me/api/portraits/women/33.jpg',
    zones: {
      hormonal: 140,
      nutritional: 90,
      emotional: 85,
      cognitive: 95,
      physical: 70,
      spiritual: 65,
      social: 55
    }
  },
  { 
    id: '3', 
    name: 'Jessica L.', 
    points: 380, 
    streak: 22, 
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    zones: {
      hormonal: 110,
      nutritional: 95,
      emotional: 100,
      cognitive: 75,
      physical: 65,
      spiritual: 60,
      social: 80
    }
  },
  { 
    id: '4', 
    name: 'Lisa M.', 
    points: 310, 
    streak: 18, 
    avatar: 'https://randomuser.me/api/portraits/women/17.jpg',
    zones: {
      hormonal: 90,
      nutritional: 85,
      emotional: 75,
      cognitive: 60,
      physical: 95,
      spiritual: 45,
      social: 60
    }
  },
  { 
    id: '5', 
    name: 'Rachel K.', 
    points: 290, 
    streak: 15, 
    avatar: 'https://randomuser.me/api/portraits/women/90.jpg',
    zones: {
      hormonal: 80,
      nutritional: 95,
      emotional: 65,
      cognitive: 50,
      physical: 55,
      spiritual: 75,
      social: 70
    }
  },
  { 
    id: '6', 
    name: 'Michelle P.', 
    points: 275, 
    streak: 16, 
    avatar: 'https://randomuser.me/api/portraits/women/12.jpg',
    zones: {
      hormonal: 70,
      nutritional: 85,
      emotional: 80,
      cognitive: 45,
      physical: 60,
      spiritual: 55,
      social: 75
    }
  },
  { 
    id: '7', 
    name: 'Amanda W.', 
    points: 240, 
    streak: 12, 
    avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
    zones: {
      hormonal: 65,
      nutritional: 70,
      emotional: 60,
      cognitive: 40,
      physical: 65,
      spiritual: 50,
      social: 55
    }
  },
  { 
    id: '8', 
    name: 'Nicole B.', 
    points: 210, 
    streak: 10, 
    avatar: 'https://randomuser.me/api/portraits/women/56.jpg',
    zones: {
      hormonal: 55,
      nutritional: 60,
      emotional: 50,
      cognitive: 35,
      physical: 45,
      spiritual: 40,
      social: 60
    }
  },
  { 
    id: '9', 
    name: 'Sophia G.', 
    points: 180, 
    streak: 9, 
    avatar: 'https://randomuser.me/api/portraits/women/62.jpg',
    zones: {
      hormonal: 45,
      nutritional: 55,
      emotional: 40,
      cognitive: 30,
      physical: 50,
      spiritual: 35,
      social: 40
    }
  },
  { 
    id: '10', 
    name: 'Hannah T.', 
    points: 150, 
    streak: 8, 
    avatar: 'https://randomuser.me/api/portraits/women/79.jpg',
    zones: {
      hormonal: 40,
      nutritional: 45,
      emotional: 35,
      cognitive: 25,
      physical: 40,
      spiritual: 30,
      social: 35
    }
  },
];

// Badge system types
export interface Badge {
  id: string;
  name: string;
  description: string;
  category: BadgeCategory;
  icon: string;
  color: string;
  backgroundColor: string;
  border?: string;
  gradient?: string;
  requiredProgress: number;
  maxProgress: number;
  unlocked: boolean;
  unlockedDate?: Date;
}

export type BadgeCategory = 'completion' | 'consistency' | 'engagement' | 'achievement';

// Define available badges
export const getBadges = (userProgress: any): Badge[] => {
  const completedDays = userProgress?.completedDays || [];
  const streak = userProgress?.streak || 0;
  const points = userProgress?.points || 0;
  
  return [
    // Completion Badges
    {
      id: 'first-day',
      name: 'First Step',
      description: 'Completed your first day in the program',
      category: 'completion',
      icon: '🌱',
      color: '#6E59A5',
      backgroundColor: '#E5DEFF',
      gradient: 'linear-gradient(90deg, hsla(277, 75%, 84%, 1) 0%, hsla(297, 50%, 51%, 1) 100%)',
      requiredProgress: completedDays.length > 0 ? 1 : 0,
      maxProgress: 1,
      unlocked: completedDays.length > 0,
      unlockedDate: completedDays.length > 0 ? new Date() : undefined
    },
    {
      id: 'week-complete',
      name: 'Week Warrior',
      description: 'Completed 7 days of hormone harmony',
      category: 'completion',
      icon: '🌿',
      color: '#7E69AB',
      backgroundColor: '#E5DEFF',
      gradient: 'linear-gradient(90deg, hsla(46, 73%, 75%, 1) 0%, hsla(176, 73%, 88%, 1) 100%)',
      requiredProgress: completedDays.length,
      maxProgress: 7,
      unlocked: completedDays.length >= 7
    },
    {
      id: 'halfway',
      name: 'Halfway Hero',
      description: 'Reached the halfway point in your journey',
      category: 'completion',
      icon: '🌊',
      color: '#9b87f5',
      backgroundColor: '#E5DEFF',
      gradient: 'linear-gradient(90deg, hsla(221, 45%, 73%, 1) 0%, hsla(220, 78%, 29%, 1) 100%)',
      requiredProgress: completedDays.length,
      maxProgress: 15,
      unlocked: completedDays.length >= 15
    },
    {
      id: 'program-complete',
      name: 'Journey Completer',
      description: 'Finished the entire 30-day program',
      category: 'completion',
      icon: '🏆',
      color: '#D6BCFA',
      backgroundColor: '#F1F0FB',
      gradient: 'linear-gradient(90deg, hsla(59, 86%, 68%, 1) 0%, hsla(134, 36%, 53%, 1) 100%)',
      requiredProgress: completedDays.length,
      maxProgress: 30,
      unlocked: completedDays.length >= 30
    },
    
    // Consistency Badges
    {
      id: 'three-streak',
      name: 'Habit Starter',
      description: 'Maintained a 3-day streak',
      category: 'consistency',
      icon: '🔥',
      color: '#FEC6A1',
      backgroundColor: '#FDE1D3',
      gradient: 'linear-gradient(90deg, hsla(24, 100%, 83%, 1) 0%, hsla(341, 91%, 68%, 1) 100%)',
      requiredProgress: streak,
      maxProgress: 3,
      unlocked: streak >= 3
    },
    {
      id: 'weekly-streak',
      name: 'Week Streak',
      description: 'Maintained a 7-day streak',
      category: 'consistency',
      icon: '🔥',
      color: '#FEF7CD',
      backgroundColor: '#FFF9DC',
      gradient: 'linear-gradient(90deg, hsla(39, 100%, 77%, 1) 0%, hsla(22, 90%, 57%, 1) 100%)',
      requiredProgress: streak,
      maxProgress: 7,
      unlocked: streak >= 7
    },
    {
      id: 'biweekly-streak',
      name: 'Dedicated Practitioner',
      description: 'Maintained a 14-day streak',
      category: 'consistency',
      icon: '🔥',
      color: '#F2FCE2',
      backgroundColor: '#F7FCF0',
      gradient: 'linear-gradient(90deg, hsla(139, 70%, 75%, 1) 0%, hsla(63, 90%, 76%, 1) 100%)',
      requiredProgress: streak,
      maxProgress: 14,
      unlocked: streak >= 14
    },
    {
      id: 'monthly-streak',
      name: 'Hormone Harmony Master',
      description: 'Maintained a 30-day streak',
      category: 'consistency',
      icon: '👑',
      color: '#D3E4FD',
      backgroundColor: '#E7F0FD',
      gradient: 'linear-gradient(109.6deg, rgba(223,234,247,1) 11.2%, rgba(244,248,252,1) 91.1%)',
      requiredProgress: streak,
      maxProgress: 30,
      unlocked: streak >= 30
    },
    
    // Engagement Badges
    {
      id: 'points-100',
      name: 'Point Collector',
      description: 'Earned 100 points',
      category: 'engagement',
      icon: '⭐',
      color: '#FFDEE2',
      backgroundColor: '#FEF0F2',
      gradient: 'linear-gradient(90deg, hsla(22, 100%, 78%, 1) 0%, hsla(2, 78%, 62%, 1) 100%)',
      requiredProgress: points,
      maxProgress: 100,
      unlocked: points >= 100
    },
    {
      id: 'points-250',
      name: 'Engaged Learner',
      description: 'Earned 250 points',
      category: 'engagement',
      icon: '🌟',
      color: '#D3E4FD',
      backgroundColor: '#E7F0FD',
      gradient: 'linear-gradient(to top, #accbee 0%, #e7f0fd 100%)',
      requiredProgress: points,
      maxProgress: 250,
      unlocked: points >= 250
    },
    {
      id: 'points-500',
      name: 'Hormone Expert',
      description: 'Earned 500 points',
      category: 'engagement',
      icon: '💫',
      color: '#E5DEFF',
      backgroundColor: '#F1F0FB',
      gradient: 'linear-gradient(102.3deg, rgba(147,39,143,1) 5.9%, rgba(234,172,232,1) 64%, rgba(246,219,245,1) 89%)',
      requiredProgress: points,
      maxProgress: 500,
      unlocked: points >= 500
    },
    
    // Achievement Badges
    {
      id: 'level-3',
      name: 'Hormone Scholar',
      description: 'Reached Level 3',
      category: 'achievement',
      icon: '📚',
      color: '#D6BCFA',
      backgroundColor: '#F1F0FB',
      gradient: 'linear-gradient(to top, #d299c2 0%, #fef9d7 100%)',
      requiredProgress: calculateLevel(points),
      maxProgress: 3,
      unlocked: calculateLevel(points) >= 3
    },
    {
      id: 'level-5',
      name: 'Hormone Harmony Expert',
      description: 'Reached Level 5',
      category: 'achievement',
      icon: '🧠',
      color: '#9b87f5',
      backgroundColor: '#E5DEFF',
      gradient: 'linear-gradient(180deg, rgb(254,100,121) 0%, rgb(251,221,186) 100%)',
      requiredProgress: calculateLevel(points),
      maxProgress: 5,
      unlocked: calculateLevel(points) >= 5
    }
  ];
};

// Get badges by category
export const getBadgesByCategory = (badges: Badge[], category: BadgeCategory): Badge[] => {
  return badges.filter(badge => badge.category === category);
};

// Get unlocked badges
export const getUnlockedBadges = (badges: Badge[]): Badge[] => {
  return badges.filter(badge => badge.unlocked);
};

// Get locked badges
export const getLockedBadges = (badges: Badge[]): Badge[] => {
  return badges.filter(badge => !badge.unlocked);
};

// Check if a new badge was unlocked
export const checkNewBadgeUnlocked = (oldBadges: Badge[], newBadges: Badge[]): Badge | null => {
  for (const newBadge of newBadges) {
    if (newBadge.unlocked) {
      const oldBadge = oldBadges.find(b => b.id === newBadge.id);
      if (oldBadge && !oldBadge.unlocked) {
        return newBadge;
      }
    }
  }
  return null;
};
